/**
 * Personal Details Screen for Ramps Flow
 *
 * Dedicated screen for entering personal name information
 * Part of the sequential flow: Calculator → Name → Phone → Address → ID → Review → Status
 */

import { t } from "@lingui/core/macro";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, IdCard, X } from "lucide-react";
import { useEffect, useState } from "react";
import { RampsButton } from "@/components/ramps/Button";
import { useContinue } from "@/hooks/useContinue";
import { useNavigateBack } from "@/hooks/useNavigateBack";
import { useAnalytics } from "@/hooks/usePostHog";
import { RampsInput } from "../components/ramps/Input";
import { RampsText } from "../components/ramps/Text";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerTitle,
} from "../components/ui/drawer";
import { Progress } from "../components/ui/progress";
import { nameStepSchema, useRampsForm } from "../hooks/useRampsForm";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";

export const Route = createFileRoute("/ramps/name")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			accesstoken?: string;
		},
	component: PersonalDetailsPage,
});

function PersonalDetailsPage() {
	const search = Route.useSearch(); // Get current search params
	const store = useRampsStore();
	const form = useRampsForm();
	const [showConfirmationDrawer, setShowConfirmationDrawer] = useState(false);
	const handleContinue = useContinue();
	const handleBack = useNavigateBack();
	const { trackPageView } = useAnalytics();

	// Track page view
	useEffect(() => {
		trackPageView("name", { page_name: "name" });
	}, [trackPageView]);

	// Check if profile data comes from backend (existing user profile)
	const isExistingProfile = store.quoteResponse?.isExistingProfile ?? false;

	// Token and transaction_id are synced globally by URLParamsInitializer

	const handleSubmit = () => {
		// Validate current step before proceeding
		const stepValidation = nameStepSchema.safeParse(form.state.values);

		if (stepValidation.success) {
			// Show confirmation drawer before proceeding
			setShowConfirmationDrawer(true);
		}
	};

	const handleConfirmName = () => {
		// Save name data to store first
		const { firstName, lastName, middleName, secondLastName } =
			form.state.values;
		rampsActions.setPersonalInfo({
			firstName,
			lastName,
			middleName,
			secondLastName,
		});

		// Mark name as confirmed and proceed to address
		rampsActions.confirmLegalName(true);
		rampsActions.setCurrentStep("address");
		handleContinue(search);
	};

	const handleEditName = () => {
		// Close drawer and let user edit
		setShowConfirmationDrawer(false);
		rampsActions.confirmLegalName(false);
	};

	// Helper function to get first error message from field
	const getFieldError = (fieldName: keyof typeof nameStepSchema.shape) => {
		const field = form.getFieldMeta(fieldName);
		if (field?.errors && field.errors.length > 0) {
			const error = field.errors[0];
			return typeof error === "string" ? error : error.message || String(error);
		}
		return undefined;
	};

	// Helper function to capitalize first letter of each word
	const capitalizeFirstLetter = (value: string) => {
		return value
			.split(" ")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(" ");
	};

	// Helper function to prevent numbers in name inputs
	const preventNumbers = (value: string) => {
		return value.replace(/[0-9]/g, "");
	};

	// Combined helper for name fields: capitalize and prevent numbers
	const formatNameInput = (value: string) => {
		return capitalizeFirstLetter(preventNumbers(value));
	};

	// Format display name for confirmation display
	const getDisplayName = () => {
		const { firstName, lastName, middleName, secondLastName } =
			form.state.values;
		const parts = [
			capitalizeFirstLetter(firstName),
			capitalizeFirstLetter(middleName),
			capitalizeFirstLetter(lastName),
			capitalizeFirstLetter(secondLastName),
		].filter(Boolean);
		return parts.join(" ");
	};

	return (
		<div className="flex flex-col min-h-screen max-w-screen-sm mx-auto bg-ramps-background-base">
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="flex items-center justify-center w-10 h-10 rounded-full text-ramps-text-primary hover:bg-ramps-background-surface-3 transition-colors"
				>
					<ArrowLeft className="w-5 h-5" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="px-4 mb-6">
				<Progress value={35} className="h-2" />
				<div className="flex justify-between mt-2">
					<RampsText variant="title-lg-bold" color="primary">
						{t`Your name`}
					</RampsText>
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 px-4 pb-4 flex flex-col">
				<div className="grid grid-cols-1 gap-3">
					<form.Field
						name="firstName"
						validators={{
							onChange: nameStepSchema.shape.firstName,
						}}
					>
						{(field) => (
							<RampsInput
								label={t`First Name`}
								type="text"
								value={field.state.value}
								onChange={(e) =>
									field.handleChange(formatNameInput(e.target.value))
								}
								onBlur={field.handleBlur}
								error={getFieldError("firstName")}
								disabled={isExistingProfile}
							/>
						)}
					</form.Field>
					<form.Field name="middleName">
						{(field) => (
							<RampsInput
								label={t`Middle Name (Optional)`}
								type="text"
								value={field.state.value}
								onChange={(e) =>
									field.handleChange(formatNameInput(e.target.value))
								}
								onBlur={field.handleBlur}
								error={getFieldError("middleName")}
								disabled={isExistingProfile}
							/>
						)}
					</form.Field>
					<form.Field
						name="lastName"
						validators={{
							onChange: nameStepSchema.shape.lastName,
						}}
					>
						{(field) => (
							<RampsInput
								label={t`Last Name`}
								type="text"
								value={field.state.value}
								onChange={(e) =>
									field.handleChange(formatNameInput(e.target.value))
								}
								onBlur={field.handleBlur}
								error={getFieldError("lastName")}
								disabled={isExistingProfile}
							/>
						)}
					</form.Field>
					<form.Field name="secondLastName">
						{(field) => (
							<RampsInput
								label={t`Second Last Name (Optional)`}
								type="text"
								value={field.state.value}
								onChange={(e) =>
									field.handleChange(formatNameInput(e.target.value))
								}
								onBlur={field.handleBlur}
								error={getFieldError("secondLastName")}
								disabled={isExistingProfile}
							/>
						)}
					</form.Field>
				</div>
				{/* Continue Button */}
				<div className=" mt-auto align-bottom justify-center">
					<form.Subscribe
						selector={(state) => nameStepSchema.safeParse(state.values).success}
					>
						{(isStepValid) => (
							<RampsButton
								onClick={handleSubmit}
								disabled={!isStepValid}
								className="w-full h-12 rounded-lg font-medium bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{t`Continue`}
							</RampsButton>
						)}
					</form.Subscribe>
				</div>
			</div>

			{/* Legal Name Confirmation Drawer */}
			<Drawer
				open={showConfirmationDrawer}
				onOpenChange={setShowConfirmationDrawer}
			>
				<DrawerContent className="bg-white rounded-t-2xl max-w-md mx-auto shadow-2xl">
					<DrawerTitle className="sr-only">{t`Legal Name Confirmation`}</DrawerTitle>
					{/* Drag handle */}

					<div className="px-6 pb-8 pt-2">
						{/* Close button */}
						<DrawerClose asChild>
							<button
								type="button"
								className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center hover:opacity-80 transition-opacity"
							>
								<X className="w-5 h-5 text-gray-600" />
							</button>
						</DrawerClose>

						{/* Icon */}
						<div className="flex justify-center mb-6 mt-4">
							<div className="w-16 h-16 rounded-full bg-gradient-ramps-status-l flex items-center justify-center">
								<IdCard className="w-8 h-8 text-primary" />
							</div>
						</div>

						{/* Content */}
						<div className="text-center mb-8">
							<RampsText
								variant="title-lg-bold"
								color="primary"
								className="mb-4"
							>
								{t`Is this your legal name?`}
							</RampsText>

							<RampsText
								variant="body-default"
								color="secondary"
								className="mb-6 leading-relaxed"
							>
								{t`Make sure your name matches what's on your legal ID to avoid money transfer delays.`}
							</RampsText>

							<div className="rounded-lg p-4 mb-6">
								<RampsText
									variant="title-lg-bold"
									color="primary"
									className="font-medium"
								>
									{getDisplayName()}
								</RampsText>
							</div>
						</div>

						{/* Buttons */}
						<div className="space-y-3">
							<RampsButton
								onClick={handleConfirmName}
								className="w-full h-12 rounded-lg font-medium bg-black text-white hover:bg-gray-800"
							>
								{t`Continue`}
							</RampsButton>

							<button
								type="button"
								onClick={handleEditName}
								className="w-full h-12 rounded-lg font-medium text-ramps-text-secondary hover:text-ramps-text-primary transition-colors"
							>
								<RampsText variant="body-default" color="secondary">
									{t`Edit`}
								</RampsText>
							</button>
						</div>
					</div>
				</DrawerContent>
			</Drawer>
		</div>
	);
}
